import React, { ReactElement, useContext, useEffect, useState } from "react";
import EditIcon from "@material-ui/icons/Edit";
import ViewIcon from "@material-ui/icons/Visibility";
import DeleteIcon from "@material-ui/icons/Delete";
import CustomTable, { CustomTableRowData } from "../../../components/CustomTable";
import { ProductContext, ProductContextType } from "../../../contexts/ProductContext";
import formatIdentifierAsString from "../../../api/services/common/formatIdentifierAsString";
import { WithPredefinedChip } from "../../../components/Chip/PredefinedSampleChip";
import { ProductSampleReference, UnknownProductSample } from "../../../api/models/product/ProductSampleReference";
import ProductSampleDTO from "../../../api/models/product/ProductSampleDTO";

interface UnknownProductSampleOverviewTableProps {
  onDelete: (unknownProductSample: Partial<UnknownProductSample>) => void;
  onEdit: (
    productSampleId: number,
    additionalActions?: (sampleData: ProductSampleDTO, usedIn: boolean) => void,
  ) => void;
  showProductSampleDeleteAction: (data: { id: number }) => boolean;
  showProductSampleEditAction: (data: { id: number }) => boolean;
  showProductSampleViewDetailsAction: (data: { id: number }) => boolean;
  productSampleReferences: ProductSampleReference[];
}

function isUnknownProductSampleReference(
  sampleRef: ProductSampleReference,
): sampleRef is ProductSampleReference<UnknownProductSample> {
  return sampleRef.productSample.type === "UNKNOWN_PRODUCT";
}
function isUnknownProductSample(sample: ProductSampleDTO): sample is UnknownProductSample {
  return sample.type === "UNKNOWN_PRODUCT";
}

const UnknownProductSampleOverviewTable: React.FC<UnknownProductSampleOverviewTableProps> = (
  props: UnknownProductSampleOverviewTableProps,
): ReactElement => {
  const {
    onDelete,
    onEdit: handleEditById,
    productSampleReferences,
    showProductSampleDeleteAction,
    showProductSampleEditAction,
    showProductSampleViewDetailsAction,
  } = props;
  const { updateUnknownProduct } = useContext(ProductContext) as ProductContextType & {
    updateProductObject: (productSample: UnknownProductSample & { usedIn: string | null }) => void;
  };
  const [unknownProductSampleReferences, setUnknownProductSampleReferences] = useState<
    ProductSampleReference<UnknownProductSample>[]
  >([]);

  const onEditAdditionalActions = (sampleData: ProductSampleDTO) => {
    if (isUnknownProductSample(sampleData)) {
      updateUnknownProduct({
        id: sampleData.unknownProductId,
        name: sampleData.unknownProductName,
        description: sampleData.unknownProductDescription,
      });
    }
  };

  const handleEdit = (rowData: CustomTableRowData) => {
    if (showProductSampleEditAction(rowData) || showProductSampleViewDetailsAction(rowData)) {
      handleEditById(rowData.id, onEditAdditionalActions);
    }
  };

  useEffect(() => {
    const newSampleRefs = productSampleReferences.filter(
      (psr: ProductSampleReference): psr is ProductSampleReference<UnknownProductSample> =>
        isUnknownProductSampleReference(psr),
    );
    if (JSON.stringify(newSampleRefs) !== JSON.stringify(unknownProductSampleReferences)) {
      setUnknownProductSampleReferences(newSampleRefs);
    }
  }, [productSampleReferences]);

  if (unknownProductSampleReferences.length === 0) {
    return <></>;
  }
  return (
    <CustomTable
      boldIndex={0}
      selectedItemNameValue="productSampleName"
      actionColumnIndexes={[0]}
      excludedKeys={["id", "usedIn"]}
      hasRowNumbers
      handleEdit={handleEdit}
      hasFixedColumns
      rowMenus
      header="Unknown Product Samples"
      columns={["Product Sample Name", "Unknown Product Name", "Product Description", "Delivered by"]}
      columnMaxWidths={["5rem", "Unknown Product Name", "Product Description", "Delivered by"]}
      rowMenuActions={[
        {
          action: (rowData): void => handleEditById(rowData.id, onEditAdditionalActions),
          icon: <EditIcon />,
          text: "Edit",
          show: showProductSampleEditAction,
        },
        {
          action: (rowData): void => handleEditById(rowData.id, onEditAdditionalActions),
          icon: <ViewIcon />,
          text: "View Details",
          show: showProductSampleViewDetailsAction,
        },
        {
          action: (rowData): void => onDelete(rowData),
          icon: <DeleteIcon />,
          text: "Delete",
          show: showProductSampleDeleteAction,
        },
      ]}
      rows={unknownProductSampleReferences.map((psr, index: number) => {
        const { usedIn, productSample } = psr;
        return {
          productSampleName: usedIn ? (
            <WithPredefinedChip text={productSample.productSampleName} />
          ) : (
            productSample.productSampleName
          ),
          productName: productSample.unknownProductName,
          description: productSample.unknownProductDescription,
          deliveredBy: formatIdentifierAsString(productSample.deliveredBy || ""),
          id: productSample.id || 0,
          rowNumber: index + 1,
          usedIn,
        };
      })}
    />
  );
};

export default UnknownProductSampleOverviewTable;
